/**
 * AccountDetailsPanel - Shared component for displaying account details
 * Used in: View popup, Dispute wizard step 1, Bureau comparison popup
 * 
 * Shows normalized account data with consistent labels and fallback rules:
 * - If a field is missing from the report: "Not Reported"
 * - If a bureau-specific value is missing: "No Data"
 * 
 * Bureau Transparency:
 * - When showing single-bureau summary, displays clear bureau source label
 * - Default precedence: TransUnion (first column in PDF)
 */

import React from 'react';
import { CreditCard, AlertTriangle, Info, Scale } from 'lucide-react';
import { cn } from '../../lib/utils';
import { isNegativeAccount } from '../../utils/negativeAccountClassifier';

// Canonical field definitions with display labels
const ACCOUNT_FIELDS = [
  { key: 'account_number', canonical: ['account_number_masked', 'masked_account_number', 'account_number'], label: 'Account Number' },
  { key: 'account_type', canonical: ['account_type'], label: 'Account Type' },
  { key: 'account_type_detail', canonical: ['account_type_detail', 'type_detail'], label: 'Account Type Detail' },
  { key: 'responsibility', canonical: ['responsibility', 'ownership'], label: 'Responsibility' },
  { key: 'account_status', canonical: ['account_status', 'status'], label: 'Account Status' },
  { key: 'payment_status', canonical: ['payment_status'], label: 'Payment Status' },
  { key: 'balance', canonical: ['balance'], label: 'Balance', format: 'currency' },
  { key: 'credit_limit', canonical: ['credit_limit', 'limit'], label: 'Credit Limit', format: 'currency' },
  { key: 'high_credit', canonical: ['high_credit', 'highest_credit'], label: 'High Credit', format: 'currency' },
  { key: 'monthly_payment', canonical: ['monthly_payment'], label: 'Monthly Payment', format: 'currency' },
  { key: 'past_due', canonical: ['past_due', 'past_due_amount', 'amount_past_due'], label: 'Past Due', format: 'currency' },
  { key: 'date_opened', canonical: ['date_opened'], label: 'Date Opened' },
  { key: 'last_reported', canonical: ['last_reported', 'date_reported'], label: 'Last Reported' },
  { key: 'date_last_active', canonical: ['date_last_active', 'last_active'], label: 'Date Last Active' },
  { key: 'date_last_payment', canonical: ['date_of_last_payment', 'date_last_payment', 'last_payment_date'], label: 'Date of Last Payment' },
  { key: 'original_creditor', canonical: ['original_creditor'], label: 'Original Creditor' },
  { key: 'remarks', canonical: ['remarks', 'comments', 'comment'], label: 'Comments / Remarks' },
];

// Get value with fallbacks
const getFieldValue = (account, canonicalKeys) => {
  if (!account) return null;
  for (const key of canonicalKeys) {
    const value = account[key];
    if (value !== undefined && value !== null && value !== '') {
      return value;
    }
  }
  return null;
};

// Format value based on type
const formatValue = (value, format, isBureauSpecific = false) => {
  if (value === null || value === undefined || value === '') {
    return isBureauSpecific ? 'No Data' : 'Not Reported';
  }
  
  if (format === 'currency') {
    const num = typeof value === 'string' ? parseFloat(value.replace(/[^0-9.-]/g, '')) : value;
    if (isNaN(num)) return value;
    return `$${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
  
  return String(value);
};

// Status color helper
const getStatusColor = (status) => {
  if (!status) return 'text-slate-600';
  // Ensure status is a string before calling toLowerCase
  const s = String(status).toLowerCase();
  if (s === 'open' || s === 'current' || s.includes('paid')) return 'text-emerald-600';
  if (s.includes('derogatory') || s.includes('collection') || s.includes('chargeoff') || s.includes('charge-off')) return 'text-red-600';
  if (s.includes('late') || s.includes('past due')) return 'text-amber-600';
  if (s === 'closed') return 'text-slate-500';
  return 'text-slate-600';
};

// Field Row Component - Uses CSS grid for proper column constraints
const FieldRow = ({ label, value, format, highlight, isBureauSpecific }) => {
  const formattedValue = formatValue(value, format, isBureauSpecific);
  const isNotReported = formattedValue === 'Not Reported' || formattedValue === 'No Data';
  
  return (
    <div className="grid grid-cols-[minmax(120px,1fr)_minmax(60px,auto)] gap-2 py-2 border-b border-slate-100 last:border-0 w-full">
      <span className="text-sm text-slate-500 truncate">{label}</span>
      <span 
        data-testid={`field-value-${label.toLowerCase().replace(/\s+/g, '-')}`}
        className={cn(
          'text-sm font-medium text-right',
          isNotReported ? 'text-slate-400 italic' : (highlight ? getStatusColor(value) : 'text-stone-900')
        )}
        title={formattedValue}
      >
        {formattedValue}
      </span>
    </div>
  );
};

/**
 * Bureau Source Label Component
 * Shows which bureau's data is being displayed in single-bureau summary views
 */
const BureauSourceLabel = ({ bureau = 'TransUnion', showHelperNote = true }) => {
  return (
    <div 
      className="mb-4 p-3 bg-emerald-50 border border-emerald-100 rounded-lg"
      data-testid="bureau-source-label"
    >
      <div className="flex items-center gap-2">
        <Info size={16} className="text-emerald-600 flex-shrink-0" />
        <div className="flex-1">
          <p className="text-sm font-medium text-emerald-800">
            Showing {bureau}-reported values
          </p>
          {showHelperNote && (
            <p className="text-xs text-emerald-600 mt-0.5">
              Some fields may differ across bureaus. See comparison table below.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

// Main AccountDetailsPanel Component
export const AccountDetailsPanel = ({ 
  account, 
  showHeader = true, 
  compact = false,
  bureauContext = null, // If set, use "No Data" for missing values
  showBureauSourceLabel = false, // NEW: Show bureau source transparency label
  primaryBureau = 'TransUnion', // NEW: Which bureau's values are shown
  className 
}) => {
  if (!account) return null;

  const isBureauSpecific = !!bureauContext;
  
  // Get account name
  const accountName = getFieldValue(account, ['account_name', 'creditor_name']) || 'Unknown Account';
  const isNegative = isNegativeAccount(account);

  // Get all field values
  const fieldValues = {};
  ACCOUNT_FIELDS.forEach(field => {
    fieldValues[field.key] = getFieldValue(account, field.canonical);
  });

  // Special handling for responsibility - common mislabeling fix
  // If the value looks like a responsibility (Individual, Joint, Authorized User), use it
  const responsibilityValue = fieldValues.responsibility;
  const bureauCodeValue = getFieldValue(account, ['bureau_code', 'bureau']);
  
  // Check if bureau_code actually contains responsibility data (common parsing error)
  const responsibilityTerms = ['individual', 'joint', 'authorized', 'user', 'co-signer', 'cosigner'];
  if (!responsibilityValue && bureauCodeValue) {
    const lowerBureau = bureauCodeValue.toLowerCase();
    if (responsibilityTerms.some(term => lowerBureau.includes(term))) {
      fieldValues.responsibility = bureauCodeValue;
    }
  }

  return (
    <div className={cn('bg-white w-full max-w-full overflow-x-hidden', className)}>
      {/* Bureau Source Label - When not in bureau comparison mode */}
      {showBureauSourceLabel && !bureauContext && (
        <BureauSourceLabel bureau={primaryBureau} showHelperNote={true} />
      )}

      {/* Header */}
      {showHeader && (
        <div className="flex items-center gap-3 pb-4 mb-4 border-b border-slate-200">
          <div className={cn(
            'w-10 h-10 rounded-lg flex items-center justify-center',
            isNegative ? 'bg-red-50' : 'bg-emerald-50'
          )}>
            {isNegative ? (
              <AlertTriangle size={20} className="text-red-500" />
            ) : (
              <CreditCard size={20} className="text-emerald-500" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-stone-900 truncate">{accountName}</h3>
            <p className="text-sm text-slate-500">{fieldValues.account_number || '****'}</p>
          </div>
          {isNegative && (
            <span className="px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-600">
              Negative
            </span>
          )}
          {bureauContext && (
            <span className="px-2 py-1 text-xs font-medium rounded-full bg-emerald-100 text-emerald-600">
              {bureauContext}
            </span>
          )}
        </div>
      )}

      {/* Account Details Grid */}
      <div className={cn('space-y-0 w-full max-w-full', compact ? '' : 'divide-y divide-slate-100')}>
        {/* Account Identification */}
        <div className={cn(compact ? 'pb-2' : 'pb-4')}>
          {!compact && (
            <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">
              Account Information
            </h4>
          )}
          <FieldRow 
            label="Account Number" 
            value={fieldValues.account_number} 
            isBureauSpecific={isBureauSpecific}
          />
          <FieldRow 
            label="Account Type" 
            value={fieldValues.account_type} 
            isBureauSpecific={isBureauSpecific}
          />
          <FieldRow 
            label="Account Type Detail" 
            value={fieldValues.account_type_detail} 
            isBureauSpecific={isBureauSpecific}
          />
          <FieldRow 
            label="Responsibility" 
            value={fieldValues.responsibility} 
            isBureauSpecific={isBureauSpecific}
          />
          <FieldRow 
            label="Original Creditor" 
            value={fieldValues.original_creditor} 
            isBureauSpecific={isBureauSpecific}
          />
        </div>

        {/* Status Section */}
        <div className={cn(compact ? 'py-2' : 'py-4')}>
          {!compact && (
            <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">
              Status
            </h4>
          )}
          <FieldRow 
            label="Account Status" 
            value={fieldValues.account_status} 
            highlight 
            isBureauSpecific={isBureauSpecific}
          />
          <FieldRow 
            label="Payment Status" 
            value={fieldValues.payment_status} 
            highlight 
            isBureauSpecific={isBureauSpecific}
          />
        </div>

        {/* Financial Section */}
        <div className={cn(compact ? 'py-2' : 'py-4')}>
          {!compact && (
            <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">
              Financials
            </h4>
          )}
          <FieldRow 
            label="Balance" 
            value={fieldValues.balance} 
            format="currency" 
            isBureauSpecific={isBureauSpecific}
          />
          <FieldRow 
            label="Credit Limit" 
            value={fieldValues.credit_limit} 
            format="currency" 
            isBureauSpecific={isBureauSpecific}
          />
          <FieldRow 
            label="High Credit" 
            value={fieldValues.high_credit} 
            format="currency" 
            isBureauSpecific={isBureauSpecific}
          />
          <FieldRow 
            label="Monthly Payment" 
            value={fieldValues.monthly_payment} 
            format="currency" 
            isBureauSpecific={isBureauSpecific}
          />
          <FieldRow 
            label="Past Due" 
            value={fieldValues.past_due} 
            format="currency" 
            highlight={fieldValues.past_due && parseFloat(String(fieldValues.past_due).replace(/[^0-9.-]/g, '')) > 0}
            isBureauSpecific={isBureauSpecific}
          />
        </div>

        {/* Dates Section */}
        <div className={cn(compact ? 'py-2' : 'py-4')}>
          {!compact && (
            <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">
              Dates
            </h4>
          )}
          <FieldRow 
            label="Date Opened" 
            value={fieldValues.date_opened} 
            isBureauSpecific={isBureauSpecific}
          />
          <FieldRow 
            label="Last Reported" 
            value={fieldValues.last_reported} 
            isBureauSpecific={isBureauSpecific}
          />
          <FieldRow 
            label="Date Last Active" 
            value={fieldValues.date_last_active} 
            isBureauSpecific={isBureauSpecific}
          />
          <FieldRow 
            label="Date of Last Payment" 
            value={fieldValues.date_last_payment} 
            isBureauSpecific={isBureauSpecific}
          />
        </div>

        {/* Notes / Remarks */}
        {fieldValues.remarks && (
          <div className={cn(compact ? 'pt-2' : 'pt-4')}>
            {!compact && (
              <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">
                Notes
              </h4>
            )}
            <div className="py-2">
              <p className="text-sm text-slate-500 mb-1">Comments / Remarks</p>
              <p className="text-sm text-slate-700 bg-slate-50 p-2 rounded break-words">
                {fieldValues.remarks}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AccountDetailsPanel;
